import { parseRHEncodingComponents, rhComponentMatchesValue } from './rh-encoding'

// Shared helpers for the attitude-state "domain" (Infinite / Curved / Embanked / ...).
// Not to be confused with DomainSet / DomainState.

export const DEFAULT_ATTITUDE_STATE_DOMAIN = 'Infinite'

export const ATTITUDE_STATE_DOMAIN_OPTIONS = [
  DEFAULT_ATTITUDE_STATE_DOMAIN,
  'Curved',
  'Embanked',
  'Drafting Variants'
]

const domainFolderKey = (value: string): string => value.toLowerCase().replace(/[\s_-]+/g, '')

/**
 * Domain of a ride height file from its path inside the selected directory
 * (`webkitRelativePath`, whose first segment is the selected folder). Files in a
 * top-level subfolder named after a domain (`Curved/`, `Drafting_Variants/`, ...)
 * belong to that domain; everything else is the default domain.
 */
export const domainFromRelativePath = (relativePath?: string | null): string => {
  const segments = (relativePath || '').replace(/\\/g, '/').split('/').filter(Boolean)
  if (segments.length < 3) return DEFAULT_ATTITUDE_STATE_DOMAIN
  const folderKey = domainFolderKey(segments[1])
  return (
    ATTITUDE_STATE_DOMAIN_OPTIONS.find(option => domainFolderKey(option) === folderKey) ||
    DEFAULT_ATTITUDE_STATE_DOMAIN
  )
}

export const normalizeAttitudeStateDomain = (domain?: string | null): string =>
  (domain || '').trim() || DEFAULT_ATTITUDE_STATE_DOMAIN

// Ride height files are namespaced per domain so that domain-duplicated states
// (which share the same suspension encoding and therefore identical file names)
// do not collide. The default domain stays flat for backward compatibility.
// Must mirror domainPathSegment in server rhRepoSet.controller.ts.
export const domainPathSegment = (domain?: string | null): string => {
  const normalized = normalizeAttitudeStateDomain(domain)
  if (normalized === DEFAULT_ATTITUDE_STATE_DOMAIN) return ''
  return normalized.replace(/[\\/]+/g, '_').replace(/\s+/g, '_')
}

// Staging archive key for a ride height file, scoped by its domain so identical
// file names in different domains remain distinct in the zip and in blob staging.
export const rhArchiveKey = (entry: { name: string; domain?: string | null }): string => {
  const segment = domainPathSegment(entry.domain)
  return segment ? `${segment}/${entry.name}` : entry.name
}

type AttitudeValues = {
  frh?: number
  rrh?: number
  front_height?: number
  rear_height?: number
  roll?: number
  yaw?: number
}

export const rhEncodingMatchesAttitude = (encoding: string, state: AttitudeValues): boolean => {
  const components = parseRHEncodingComponents(encoding)
  if (!components) return false
  const frh = state.front_height ?? state.frh ?? 0
  const rrh = state.rear_height ?? state.rrh ?? 0
  return (
    rhComponentMatchesValue(components[0], frh || 0) &&
    rhComponentMatchesValue(components[1], rrh || 0) &&
    rhComponentMatchesValue(components[2], state.roll || 0) &&
    rhComponentMatchesValue(components[3], state.yaw || 0)
  )
}

/**
 * Collect every ride height file sharing the representative file's encoding.
 * When `domain` is provided, only files tagged with that domain (untagged files
 * count as the default domain) are collected, so a domain-duplicated state does
 * not pick up its same-encoding source's files. Without `domain`, no domain
 * filtering is applied.
 */
export const collectRHFileNamesForState = (
  files: { name: string; domain?: string | null }[],
  representativeName: string,
  domain?: string | null
): string[] => {
  const repComponents = parseRHEncodingComponents(representativeName)
  if (!repComponents) return [representativeName]
  const repKey = repComponents.join('_')
  const targetDomain = domain === undefined ? null : normalizeAttitudeStateDomain(domain)
  const matches = files
    .filter(file => {
      const components = parseRHEncodingComponents(file.name)
      return (
        components !== null &&
        components.join('_') === repKey &&
        (targetDomain === null || normalizeAttitudeStateDomain(file.domain) === targetDomain)
      )
    })
    .map(file => file.name)
  return matches.length > 0 ? Array.from(new Set(matches)) : [representativeName]
}

/**
 * Resolve the ride height files for a state in the new-set import form. Files
 * uploaded for the state's own domain win; otherwise the main directory's
 * (default-domain) files are used and `archiveAliases` maps the state's
 * domain-scoped archive keys onto those entries so finalize copies them into
 * the state's domain folder.
 */
export const resolveImportStateRHFiles = (
  files: { name: string; domain?: string | null }[],
  representativeName: string,
  domain?: string | null
): { fileNames: string[]; archiveAliases: Record<string, string> } => {
  const stateDomain = normalizeAttitudeStateDomain(domain)
  const repComponents = parseRHEncodingComponents(representativeName)
  const repKey = repComponents ? repComponents.join('_') : null
  const hasOwnDomainFiles =
    repKey !== null &&
    files.some(file => {
      const components = parseRHEncodingComponents(file.name)
      return (
        components !== null &&
        components.join('_') === repKey &&
        normalizeAttitudeStateDomain(file.domain) === stateDomain
      )
    })
  const sourceDomain = hasOwnDomainFiles ? stateDomain : DEFAULT_ATTITUDE_STATE_DOMAIN
  const fileNames = collectRHFileNamesForState(files, representativeName, sourceDomain)
  const archiveAliases: Record<string, string> = {}
  if (sourceDomain !== stateDomain) {
    fileNames.forEach(name => {
      archiveAliases[rhArchiveKey({ name, domain: stateDomain })] = rhArchiveKey({
        name,
        domain: sourceDomain
      })
    })
  }
  return { fileNames, archiveAliases }
}

/**
 * Pick the RH repo for an attitude state among repos whose encoding matches it.
 * Domain-duplicated states share their source's encoding, so prefer the repo
 * created for this exact state name, then one in the same domain, and only then
 * fall back to the first encoding match.
 */
export const selectRHRepoForAttitudeState = <T extends { name?: string; domain?: string | null }>(
  rhRepos: T[],
  state: { name?: string; domain?: string | null },
  encodingMatches: (repo: T) => boolean
): T | undefined => {
  const candidates = (rhRepos || []).filter(encodingMatches)
  if (candidates.length === 0) return undefined
  const stateDomain = normalizeAttitudeStateDomain(state?.domain)
  return (
    (state?.name ? candidates.find(repo => repo.name === state.name) : undefined) ??
    candidates.find(repo => normalizeAttitudeStateDomain(repo.domain) === stateDomain) ??
    candidates[0]
  )
}
